// ===== TWO SISTERS RESIDENTIAL PRICING CALCULATOR =====

// Base rates per sqft by frequency and home condition
export const BASE_RATES = {
  super_clean: {
    label: 'Super Clean Freaks',
    description: 'Immaculate home, minimal cleaning needed',
    image: 'super_clean',
    rates: {
      deep_clean: 0.25,
      monthly: 0.08,
      biweekly: 0.06,
      weekly: 0.05
    }
  },
  second_home: {
    label: 'Clean (Second Home)',
    description: 'Rarely used, stays tidy',
    image: 'second_home',
    rates: {
      deep_clean: 0.27,
      monthly: 0.09,
      biweekly: 0.07,
      weekly: 0.05
    }
  },
  lived_in: {
    label: 'Clean but Lived In',
    description: 'Normal family home, regular use',
    image: 'lived_in',
    rates: {
      deep_clean: 0.30,
      monthly: 0.10,
      biweekly: 0.08,
      weekly: 0.06
    }
  },
  busy_home: {
    label: "There's a Lot of Us",
    description: 'Busy household, lots of activity',
    image: 'busy_home',
    rates: {
      deep_clean: 0.35,
      monthly: 0.12,
      biweekly: 0.10,
      weekly: 0.08
    }
  },
  overwhelmed: {
    label: "I'm Overwhelmed",
    description: 'Needs extra attention and care',
    image: 'overwhelmed',
    rates: {
      deep_clean: 0.40,
      monthly: 0.15,
      biweekly: 0.12,
      weekly: 0.10
    },
    requiresReview: true
  }
};

// Minimum prices by frequency
export const MINIMUMS = {
  deep_clean: 250,
  monthly: 250,
  biweekly: 185,
  weekly: 145
};

// Frequency labels for display
export const FREQUENCY_LABELS = {
  deep_clean: 'Deep Clean',
  one_time: 'One-Time Deep Clean',
  move_in: 'Move In',
  move_out: 'Move Out',
  move_in_out: 'Move In/Move Out',
  monthly: 'Monthly',
  biweekly: 'Bi-Weekly',
  weekly: 'Weekly',
  airbnb: 'Short-Term Rental'
};

// Job types that use deep clean rates
export const DEEP_CLEAN_TYPES = ['deep_clean', 'one_time', 'move_in', 'move_out', 'move_in_out'];

// Adjustment baselines and rates
export const ADJUSTMENTS = {
  bedrooms: { baseline: 3, rate: 5 },      // +$5 per BR over 3
  fullBaths: { baseline: 2, rate: 10 },    // +$10 per full bath over 2
  halfBaths: { baseline: 1, rate: 5 },     // +$5 per half bath over 1
  people: { baseline: 3, rate: 10 },       // +$10 per person over 3
  sheddingPets: { rate: 15 },              // +$15 per shedding pet
  glassShowerDoors: { rate: 10 }           // +$10 per glass shower door
};

// Add-on pricing
export const ADDONS = {
  // Windows
  interior_windows: {
    label: 'Interior Windows',
    price: 5,
    unit: 'per window',
    category: 'windows'
  },
  window_sills: {
    label: 'Window Sills',
    price: 5,
    unit: 'per sill',
    category: 'windows'
  },
  window_tracks: {
    label: 'Window Tracks',
    price: 10,
    unit: 'per track',
    category: 'windows'
  },
  blinds: {
    label: 'Blinds (Wet Wipe)',
    price: 10,
    unit: 'per blind',
    category: 'windows'
  },
  
  // Oven
  oven_needs_love: {
    label: 'Inside Oven - Needs Love',
    price: 35,
    unit: 'flat',
    category: 'appliances',
    exclusive: 'oven'
  },
  oven_extra_love: {
    label: 'Inside Oven - Needs Extra Love',
    price: 50,
    unit: 'flat',
    category: 'appliances',
    exclusive: 'oven'
  },
  oven_elbow_grease: {
    label: 'Inside Oven - Serious Elbow Grease',
    price: 100,
    unit: 'flat',
    category: 'appliances',
    exclusive: 'oven'
  },
  
  // Fridge
  fridge_quick_wipe: {
    label: 'Inside Fridge - Quick Wipe (Empty)',
    price: 25,
    unit: 'flat',
    category: 'appliances',
    exclusive: 'fridge'
  },
  fridge_semi_full: {
    label: 'Inside Fridge - Semi Full/Deep Clean',
    price: 50,
    unit: 'flat',
    category: 'appliances',
    exclusive: 'fridge'
  },
  fridge_full: {
    label: 'Inside Fridge - Full/Extra Love',
    price: 100,
    unit: 'flat',
    category: 'appliances',
    exclusive: 'fridge'
  },
  
  // Special services
  organizing: {
    label: 'Organizing Services',
    price: 65,
    unit: 'per hour',
    category: 'services'
  },
  
  // Custom quote items
  grout_cleaning: {
    label: 'Grout Cleaning',
    price: null,
    unit: 'custom',
    category: 'custom',
    requiresPhoto: true,
    requiresReview: true
  },
  extra_buildup: {
    label: 'Extra Build-Up Areas',
    price: null,
    unit: 'custom',
    category: 'custom',
    requiresPhoto: true,
    requiresReview: true
  },
  laundry: {
    label: 'Laundry Service',
    price: null,
    unit: 'custom',
    category: 'services',
    externalLink: 'https://twosisterslaundry.netlify.app/'
  }
};

// Average room sizes for exclusion calculations (sqft)
export const ROOM_SIZES = {
  bedroom: 150,
  bathroom: 60,
  half_bath: 30,
  office: 120,
  living_room: 250,
  dining_room: 150,
  kitchen: 150,
  laundry_room: 50,
  garage: 200,
  basement: 400,
  bonus_room: 200
};

/**
 * Calculate the base price before add-ons
 * @param {Object} params - Calculation parameters
 * @returns {Object} - Calculation result with breakdown
 */
export function calculateBasePrice({
  sqft,
  condition,
  frequency,
  bedrooms = 3,
  fullBaths = 2,
  halfBaths = 1,
  people = 3,
  sheddingPets = 0,
  glassShowerDoors = 0,
  excludedRooms = []
}) {
  // Handle Airbnb/STR - no auto-pricing
  if (frequency === 'airbnb') {
    return {
      requiresCustomQuote: true,
      reason: 'Short-term rentals require a custom quote',
      lineItems: []
    };
  }

  // Get the rate category (deep_clean types all use deep_clean rates)
  const rateCategory = DEEP_CLEAN_TYPES.includes(frequency) ? 'deep_clean' : frequency;
  
  // Get condition config
  const conditionConfig = BASE_RATES[condition];
  if (!conditionConfig) {
    throw new Error(`Invalid condition: ${condition}`);
  }

  // Calculate excluded sqft
  const excludedSqft = excludedRooms.reduce((total, room) => {
    return total + (ROOM_SIZES[room] || 0);
  }, 0);
  
  const adjustedSqft = Math.max(sqft - excludedSqft, 500); // Minimum 500 sqft

  // Calculate base price from sqft
  const rate = conditionConfig.rates[rateCategory];
  let baseFromSqft = adjustedSqft * rate;
  
  // Apply minimum
  const minimum = MINIMUMS[rateCategory];
  const basePrice = Math.max(baseFromSqft, minimum);
  const minimumApplied = baseFromSqft < minimum;

  // Calculate adjustments
  const adjustmentDetails = [];
  let totalAdjustments = 0;

  // Bedrooms over baseline
  const extraBedrooms = Math.max(0, bedrooms - ADJUSTMENTS.bedrooms.baseline);
  if (extraBedrooms > 0) {
    const amount = extraBedrooms * ADJUSTMENTS.bedrooms.rate;
    adjustmentDetails.push({
      label: `Extra Bedrooms (${extraBedrooms})`,
      amount
    });
    totalAdjustments += amount;
  }

  // Full baths over baseline
  const extraFullBaths = Math.max(0, fullBaths - ADJUSTMENTS.fullBaths.baseline);
  if (extraFullBaths > 0) {
    const amount = extraFullBaths * ADJUSTMENTS.fullBaths.rate;
    adjustmentDetails.push({
      label: `Extra Full Bathrooms (${extraFullBaths})`,
      amount
    });
    totalAdjustments += amount;
  }

  // Half baths over baseline
  const extraHalfBaths = Math.max(0, halfBaths - ADJUSTMENTS.halfBaths.baseline);
  if (extraHalfBaths > 0) {
    const amount = extraHalfBaths * ADJUSTMENTS.halfBaths.rate;
    adjustmentDetails.push({
      label: `Extra Half Bathrooms (${extraHalfBaths})`,
      amount
    });
    totalAdjustments += amount;
  }

  // People over baseline
  const extraPeople = Math.max(0, people - ADJUSTMENTS.people.baseline);
  if (extraPeople > 0) {
    const amount = extraPeople * ADJUSTMENTS.people.rate;
    adjustmentDetails.push({
      label: `Extra Occupants (${extraPeople})`,
      amount
    });
    totalAdjustments += amount;
  }

  // Shedding pets
  if (sheddingPets > 0) {
    const amount = sheddingPets * ADJUSTMENTS.sheddingPets.rate;
    adjustmentDetails.push({
      label: `Shedding Pets (${sheddingPets})`,
      amount
    });
    totalAdjustments += amount;
  }

  // Glass shower doors
  if (glassShowerDoors > 0) {
    const amount = glassShowerDoors * ADJUSTMENTS.glassShowerDoors.rate;
    adjustmentDetails.push({
      label: `Glass Shower Doors (${glassShowerDoors})`,
      amount
    });
    totalAdjustments += amount;
  }

  // Build line items
  const lineItems = [
    {
      id: 'base_service',
      type: 'base',
      label: `${FREQUENCY_LABELS[frequency]} Service`,
      description: `${adjustedSqft.toLocaleString()} sqft @ $${rate.toFixed(2)}/sqft`,
      price: basePrice,
      required: true,
      selected: true
    }
  ];

  // Add adjustments as a single line item if any
  if (totalAdjustments > 0) {
    lineItems.push({
      id: 'adjustments',
      type: 'adjustment',
      label: 'Home Adjustments',
      description: adjustmentDetails.map(a => a.label).join(', '),
      price: totalAdjustments,
      required: true,
      selected: true,
      details: adjustmentDetails
    });
  }

  // Check if manager review required
  const requiresReview = conditionConfig.requiresReview || false;

  return {
    sqft: adjustedSqft,
    originalSqft: sqft,
    excludedSqft,
    condition,
    conditionLabel: conditionConfig.label,
    frequency,
    frequencyLabel: FREQUENCY_LABELS[frequency],
    rate,
    baseFromSqft,
    minimum,
    minimumApplied,
    basePrice,
    adjustments: adjustmentDetails,
    totalAdjustments,
    subtotal: basePrice + totalAdjustments,
    lineItems,
    requiresReview,
    reviewReason: requiresReview ? 'Home condition requires manager review' : null
  };
}

/**
 * Calculate add-on prices
 * @param {Array} selectedAddons - Array of { addonId, quantity } objects
 * @returns {Object} - Add-on calculation result
 */
export function calculateAddons(selectedAddons = []) {
  const lineItems = [];
  let total = 0;
  let requiresReview = false;
  const reviewReasons = [];

  selectedAddons.forEach(({ addonId, quantity = 1, notes, photos }) => {
    const addon = ADDONS[addonId];
    if (!addon) return;

    if (addon.requiresReview) {
      requiresReview = true;
      reviewReasons.push(`${addon.label} requires custom quote`);
      
      lineItems.push({
        id: addonId,
        type: 'addon',
        label: addon.label,
        description: 'Custom quote required',
        price: null,
        priceDisplay: 'TBD',
        quantity,
        required: false,
        selected: true,
        requiresReview: true,
        notes,
        photos
      });
    } else if (addon.price !== null) {
      const itemTotal = addon.price * quantity;
      total += itemTotal;
      
      lineItems.push({
        id: addonId,
        type: 'addon',
        label: addon.label,
        description: addon.unit === 'flat' ? null : `${quantity} ${addon.unit.replace('per ', '')}${quantity > 1 ? 's' : ''}`,
        unitPrice: addon.price,
        quantity,
        price: itemTotal,
        required: false,
        selected: true
      });
    }
  });

  return {
    lineItems,
    total,
    requiresReview,
    reviewReasons
  };
}

/**
 * Generate a complete estimate
 * @param {Object} baseParams - Parameters for base price calculation
 * @param {Array} selectedAddons - Selected add-ons
 * @returns {Object} - Complete estimate
 */
export function generateEstimate(baseParams, selectedAddons = []) {
  const baseCalc = calculateBasePrice(baseParams);
  
  // If requires custom quote (e.g., Airbnb), return early
  if (baseCalc.requiresCustomQuote) {
    return baseCalc;
  }

  const addonCalc = calculateAddons(selectedAddons);

  // For recurring frequencies, addons only apply to deep clean, NOT recurring price
  // For one-time/deep clean frequencies, addons apply to the main total
  const isDeepCleanType = DEEP_CLEAN_TYPES.includes(baseParams.frequency);
  
  // Recurring line items should NOT include addons
  const lineItems = isDeepCleanType 
    ? [...baseCalc.lineItems, ...addonCalc.lineItems]  // One-time: include addons in main
    : baseCalc.lineItems;                                // Recurring: base only

  // Recurring total should NOT include addon prices
  const calculatedTotal = isDeepCleanType
    ? baseCalc.subtotal + addonCalc.total   // One-time: include addons
    : baseCalc.subtotal;                     // Recurring: base only

  // Determine if review required
  const requiresReview = baseCalc.requiresReview || addonCalc.requiresReview;
  const reviewReasons = [];
  if (baseCalc.reviewReason) reviewReasons.push(baseCalc.reviewReason);
  if (addonCalc.reviewReasons) reviewReasons.push(...addonCalc.reviewReasons);

  // If this is for recurring service, also calculate deep clean price
  let deepCleanEstimate = null;
  if (!isDeepCleanType) {
    deepCleanEstimate = calculateBasePrice({
      ...baseParams,
      frequency: 'deep_clean'
    });
    // Add addon line items to deep clean estimate
    if (addonCalc.lineItems.length > 0) {
      deepCleanEstimate.lineItems = [
        ...(deepCleanEstimate.lineItems || []),
        ...addonCalc.lineItems.map(item => ({ ...item, type: 'addon' }))
      ];
      deepCleanEstimate.subtotal += addonCalc.total;
    }
  }

  return {
    ...baseCalc,
    addons: addonCalc,
    lineItems,
    total: calculatedTotal,
    hasCustomItems: addonCalc.lineItems.some(li => li.requiresReview),
    requiresReview,
    reviewReasons,
    deepCleanEstimate
  };
}

/**
 * Format price for display
 * @param {number} price - Price to format
 * @returns {string} - Formatted price string
 */
export function formatPrice(price) {
  if (price === null || price === undefined) return 'TBD';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(price);
}

/**
 * Calculate what the recurring price would be for different frequencies
 * @param {Object} baseParams - Base calculation parameters (with deep_clean frequency)
 * @returns {Object} - Prices for each frequency
 */
export function calculateAllFrequencies(baseParams) {
  const frequencies = ['weekly', 'biweekly', 'monthly', 'deep_clean'];
  const results = {};

  frequencies.forEach(freq => {
    const calc = calculateBasePrice({ ...baseParams, frequency: freq });
    results[freq] = {
      label: FREQUENCY_LABELS[freq],
      subtotal: calc.subtotal,
      minimum: MINIMUMS[freq === 'deep_clean' ? 'deep_clean' : freq],
      minimumApplied: calc.minimumApplied
    };
  });

  return results;
}
